import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { Check, Send, UserPlus, HelpCircle, MapPin, Clock, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { getLangFromPath, localizePath } from '../i18n';

const EVENT_TYPE_KEYS = ['deportes', 'social', 'corporativo', 'recital', 'hospitality', 'marca', 'otro'];
const ATTENDEE_VALUES = ['< 500', '500 - 2.000', '2.000 - 10.000', '10.000 - 50.000', '50.000+'];

export default function RequestPhotographers() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '',
    eventType: '', attendees: '', location: '', eventDate: '',
    photographerCount: '', countUnknown: false,
    photoModel: '', revenueShare: '70', fixedFee: '150', pricingUnknown: false,
    coordinationLevel: 'full', notes: '',
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const f = (k) => t(`requestDemo.form.${k}`);
  const p = (k) => t(`requestPhotographers.${k}`);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast({ title: f('missing'), description: f('missingDesc') });
      return;
    }
    if (!form.photoModel) {
      toast({ title: p('missingModel'), description: p('missingModelDesc') });
      return;
    }
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'request-photographers',
          name: form.name, email: form.email, company: form.company,
          phone: form.phone, eventType: form.eventType, attendees: form.attendees,
          location: form.location, eventDate: form.eventDate, message: form.notes,
          needPhotographers: true,
          photographerCount: form.photographerCount, countUnknown: form.countUnknown,
          photoModel: form.photoModel, revenueShare: form.revenueShare,
          fixedFee: form.fixedFee, pricingUnknown: form.pricingUnknown,
          coordinationLevel: form.coordinationLevel,
          sourceUrl: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || 'Error');
      }
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({ event: 'lead_form_submit', form: 'request-photographers', event_type: form.eventType });
      }
      setDone(true);
      toast({ title: p('sent'), description: p('sentDesc') });
    } catch (err) {
      toast({ title: p('error'), description: p('errorDesc') });
      console.error('Lead submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { icon: MapPin, key: 'benefit1' },
    { icon: Clock, key: 'benefit2' },
    { icon: ShieldCheck, key: 'benefit3' },
    { icon: Sparkles, key: 'benefit4' },
  ];

  return (
    <>
      <SEO
        title={p('seoTitle')}
        description={p('seoDescription')}
        path="/solicitar-fotografos"
      />
      <section className="relative overflow-hidden py-16 lg:py-24">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 left-1/3 w-[600px] h-[600px] bg-indigo-600/15 rounded-full blur-[140px]" />
          <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-fuchsia-500/12 rounded-full blur-[140px]" />
        </div>
        <div className="container-mi relative">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 mb-4">{p('eyebrow')}</div>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">
                {p('titlePart1')}
                <span className="block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent">
                  {p('titleHighlight')}
                </span>
              </h1>
              <p className="mt-5 text-lg text-white/70 leading-relaxed">
                {p('subtitle')}{' '}
                <Link to={localizePath('/request-demo', lang)} className="text-indigo-300 underline hover:text-indigo-200">
                  {p('subtitleLink')}
                </Link>.
              </p>

              <ul className="mt-10 space-y-4">
                {benefits.map(({ icon: Icon, key }) => (
                  <li key={key} className="flex items-center gap-3 text-white/80">
                    <div className="w-9 h-9 rounded-lg bg-indigo-500/10 border border-indigo-400/25 flex items-center justify-center text-indigo-300">
                      <Icon className="w-4 h-4" />
                    </div>
                    {p(key)}
                  </li>
                ))}
              </ul>

              <div className="mt-10 flex items-center gap-3 p-4 rounded-2xl bg-[#111116] border border-white/8">
                <UserPlus className="w-5 h-5 text-indigo-300" />
                <div className="text-sm text-white/70">{p('briefNote')}</div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl bg-[#111116] border border-white/8 p-8 md:p-10">
                {done ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-indigo-500/15 border border-indigo-400/30 flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-indigo-300" />
                    </div>
                    <h2 className="font-display text-2xl font-bold mb-3">
                      {t('requestDemo.form.thanks', { name: form.name.split(' ')[0] })}
                    </h2>
                    <p className="text-white/70 max-w-md mx-auto">
                      {t('requestPhotographers.thanksDesc', { email: form.email })}
                    </p>
                    <Link to={localizePath('/', lang)} className="inline-block mt-8">
                      <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10">
                        {f('backHome')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="name" className="text-sm text-white/70">{f('name')} *</Label>
                        <Input id="name" value={form.name} onChange={(e) => update('name', e.target.value)} placeholder={f('namePlaceholder')} className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-sm text-white/70">{f('email')} *</Label>
                        <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder={f('emailPlaceholder')} className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <Label htmlFor="company" className="text-sm text-white/70">{f('company')} *</Label>
                        <Input id="company" value={form.company} onChange={(e) => update('company', e.target.value)} placeholder={f('companyPlaceholder')} className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-sm text-white/70">{f('phone')}</Label>
                        <Input id="phone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder={f('phonePlaceholder')} className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                      </div>
                    </div>

                    <div className="pt-2">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-3">{p('sectionEvent')}</div>
                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <Label className="text-sm text-white/70">{f('eventType')}</Label>
                          <Select value={form.eventType} onValueChange={(v) => update('eventType', v)}>
                            <SelectTrigger className="mt-2 bg-[#0a0a0f] border-white/10 h-11">
                              <SelectValue placeholder={f('selectOption')} />
                            </SelectTrigger>
                            <SelectContent>
                              {EVENT_TYPE_KEYS.map((k) => (
                                <SelectItem key={k} value={k}>{t(`requestPhotographers.eventTypes.${k}`)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm text-white/70">{p('attendeesLabel')}</Label>
                          <Select value={form.attendees} onValueChange={(v) => update('attendees', v)}>
                            <SelectTrigger className="mt-2 bg-[#0a0a0f] border-white/10 h-11">
                              <SelectValue placeholder={f('attendeesPlaceholder')} />
                            </SelectTrigger>
                            <SelectContent>
                              {ATTENDEE_VALUES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-5 mt-5">
                        <div>
                          <Label htmlFor="location" className="text-sm text-white/70">{f('location')}</Label>
                          <Input id="location" value={form.location} onChange={(e) => update('location', e.target.value)} placeholder={p('locationPlaceholder')} className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                        </div>
                        <div>
                          <Label htmlFor="eventDate" className="text-sm text-white/70">{p('eventDate')}</Label>
                          <Input id="eventDate" type="date" value={form.eventDate} onChange={(e) => update('eventDate', e.target.value)} className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                        </div>
                      </div>
                    </div>

                    <div className="pt-4">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-3">{p('sectionPhotographers')}</div>
                      <div>
                        <Label className="text-sm text-white/70">{f('photographerCount')}</Label>
                        <div className="mt-2 grid grid-cols-[1fr_auto] gap-3">
                          <Input type="number" min="1" value={form.photographerCount} onChange={(e) => update('photographerCount', e.target.value)} disabled={form.countUnknown} placeholder={f('countPlaceholder')} className="bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500" />
                          <label className="flex items-center gap-2 px-3 rounded-lg bg-[#0a0a0f] border border-white/10 text-[13px] text-white/70 cursor-pointer">
                            <input type="checkbox" checked={form.countUnknown} onChange={(e) => update('countUnknown', e.target.checked)} className="w-3.5 h-3.5 accent-indigo-500 cursor-pointer" />
                            {f('countUnknown')}
                          </label>
                        </div>
                      </div>

                      <div className="mt-5">
                        <Label className="text-sm text-white/70">{f('photoModelQ')}</Label>
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          {[
                            { value: 'vender', labelKey: 'sell', descKey: 'sellDesc' },
                            { value: 'bonificar', labelKey: 'gift', descKey: 'giftDesc' },
                            { value: 'unsure', labelKey: 'unsure', descKey: 'unsureDesc' },
                          ].map((opt) => (
                            <button
                              key={opt.value} type="button"
                              data-testid={`photo-model-${opt.value}`}
                              onClick={() => update('photoModel', opt.value)}
                              className={`rounded-xl border p-3 text-left transition-all ${form.photoModel === opt.value ? 'border-indigo-400/60 bg-indigo-500/15' : 'border-white/10 bg-[#0a0a0f] hover:border-white/20'}`}
                            >
                              <div className="text-sm font-semibold text-white">{f(opt.labelKey)}</div>
                              <div className="text-[10px] text-white/50 mt-0.5">{f(opt.descKey)}</div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {form.photoModel === 'vender' && (
                        <div className="mt-5">
                          <Label className="text-sm text-white/70 flex items-center justify-between">
                            <span>{f('revenueShare')}</span>
                            <span className="font-display text-lg text-white font-bold">{form.revenueShare}%</span>
                          </Label>
                          <input type="range" min="0" max="100" value={form.revenueShare} onChange={(e) => update('revenueShare', e.target.value)} disabled={form.pricingUnknown} data-testid="revenue-share-slider" className="mt-3 w-full h-1.5 rounded-full appearance-none cursor-pointer disabled:opacity-40" style={{ background: `linear-gradient(to right, rgb(129 140 248) 0%, rgb(167 139 250) ${form.revenueShare}%, rgba(255,255,255,0.08) ${form.revenueShare}%, rgba(255,255,255,0.08) 100%)` }} />
                          <div className="mt-1 flex items-center justify-between text-[10px] text-white/40">
                            <span>0%</span>
                            <span className="text-indigo-300">{f('recommendedRange')}</span>
                            <span>100%</span>
                          </div>
                        </div>
                      )}

                      {form.photoModel === 'bonificar' && (
                        <div className="mt-5">
                          <Label htmlFor="fixedFee" className="text-sm text-white/70">{f('fixedFee')}</Label>
                          <Input id="fixedFee" type="number" min="0" value={form.fixedFee} onChange={(e) => update('fixedFee', e.target.value)} disabled={form.pricingUnknown} placeholder="150" className="mt-2 bg-[#0a0a0f] border-white/10 h-11 focus-visible:ring-indigo-500 disabled:opacity-40" />
                          <div className="text-[10px] text-indigo-300 mt-1">{f('avgLatam')}</div>
                        </div>
                      )}

                      {(form.photoModel === 'vender' || form.photoModel === 'bonificar') && (
                        <label className="mt-4 flex items-center gap-2 text-[13px] text-white/60 cursor-pointer">
                          <input type="checkbox" checked={form.pricingUnknown} onChange={(e) => update('pricingUnknown', e.target.checked)} className="w-3.5 h-3.5 accent-indigo-500 cursor-pointer" />
                          {f('pricingUnknown')}
                        </label>
                      )}

                      {form.photoModel === 'unsure' && (
                        <div className="mt-5 rounded-xl bg-indigo-500/10 border border-indigo-400/25 px-4 py-3 flex items-start gap-3">
                          <HelpCircle className="w-4 h-4 text-indigo-300 mt-0.5 shrink-0" />
                          <div className="text-[13px] text-white/80 leading-relaxed">{f('unsureNote')}</div>
                        </div>
                      )}
                    </div>

                    <div className="pt-4">
                      <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-3">{p('sectionCoordination')}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {[
                          { value: 'full', labelKey: 'fullCoord', descKey: 'fullCoordDesc' },
                          { value: 'shortlist', labelKey: 'shortlistCoord', descKey: 'shortlistCoordDesc' },
                        ].map((opt) => (
                          <button
                            key={opt.value} type="button"
                            data-testid={`coordination-${opt.value}`}
                            onClick={() => update('coordinationLevel', opt.value)}
                            className={`rounded-xl border p-4 text-left transition-all ${form.coordinationLevel === opt.value ? 'border-indigo-400/60 bg-indigo-500/15' : 'border-white/10 bg-[#0a0a0f] hover:border-white/20'}`}
                          >
                            <div className="text-sm font-semibold text-white">{f(opt.labelKey)}</div>
                            <div className="text-[11px] text-white/55 mt-1 leading-snug">{f(opt.descKey)}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-sm text-white/70">{p('notes')}</Label>
                      <Textarea id="notes" value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder={p('notesPlaceholder')} rows={3} className="mt-2 bg-[#0a0a0f] border-white/10 focus-visible:ring-indigo-500 resize-none" />
                    </div>

                    <Button type="submit" disabled={submitting} data-testid="request-photographers-submit" className="w-full h-12 rounded-full font-semibold text-white bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:brightness-110">
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          {p('submitting')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          {p('submit')} <Send className="w-4 h-4" />
                        </span>
                      )}
                    </Button>

                    <p className="text-xs text-white/40 text-center">{p('privacyNote')}</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
